import Phaser from 'phaser';
import { getCutscene, type CutscenePanel } from '@/data/cutscene';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

const DEFAULT_CUTSCENE_ID = 'stage1-end';
const DEFAULT_NEXT_SCENE = 'ResultsScene';

/**
 * EndCutsceneScene — MY-1450 / MY-1663
 *
 * Reusable comic-book end cutscene. Rendered procedurally (no sprites).
 * Pass { cutsceneId, nextSceneKey } via scene.start() to customise per stage.
 * Defaults to Stage 1 Ghost reveal + ResultsScene for backward compatibility.
 * Unlocks Ghost in the active save slot on scene entry (Stage 1 only; later
 * stages may pass their own unlock logic before starting this scene).
 * Advance: any key or pointer click. Transitions to nextSceneKey after last panel.
 */
export class EndCutsceneScene extends Phaser.Scene {
  private panels: readonly CutscenePanel[] = [];
  private nextSceneKey = DEFAULT_NEXT_SCENE;
  private nextData?: Record<string, unknown>;
  private panelIndex = 0;
  private advancing = false;

  constructor() {
    super({ key: 'EndCutsceneScene' });
  }

  init(data: {
    cutsceneId?: string;
    nextSceneKey?: string;
    nextData?: Record<string, unknown>;
  }): void {
    const id = data?.cutsceneId ?? DEFAULT_CUTSCENE_ID;
    this.panels = getCutscene(id);
    this.nextSceneKey = data?.nextSceneKey ?? DEFAULT_NEXT_SCENE;
    this.nextData = data?.nextData;
  }

  create(): void {
    this.panelIndex = 0;
    this.advancing = false;

    // Unlock Ghost as soon as the cutscene begins (Bargee KO has been confirmed).
    getSaveManager().unlockHero(ACTIVE_SLOT, 'ghost', Date.now());

    this.renderPanel(0);

    this.input.keyboard?.on('keydown', () => this.advance());
    this.input.on('pointerdown', () => this.advance());
  }

  private renderPanel(index: number): void {
    this.tweens.killAll();
    this.children.removeAll(true);
    this.cameras.main.setAlpha(1);

    const panel = this.panels[index];
    if (!panel) {
      this.endCutscene();
      return;
    }

    const { width, height } = this.scale;
    this.buildPanelVisuals(panel, width, height, index);
    this.fadeIn();
  }

  private buildPanelVisuals(
    panel: CutscenePanel,
    width: number,
    height: number,
    index: number,
  ): void {
    const accentHex = `#${panel.accentColor.toString(16).padStart(6, '0')}`;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, panel.bgColor);

    // Outer comic border
    const border = this.add.graphics();
    border.lineStyle(6, panel.accentColor, 0.85);
    border.strokeRect(14, 14, width - 28, height - 28);

    // Top accent strip
    this.add.rectangle(width / 2, 42, width - 28, 56, panel.accentColor).setAlpha(0.12);

    // Heading
    this.add
      .text(width / 2, 44, panel.heading, {
        fontFamily: FONT_IMPACT,
        fontSize: '28px',
        color: accentHex,
        letterSpacing: 4,
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    // Inner content tint
    this.add
      .rectangle(width / 2, height * 0.5 + 10, width - 80, height - 120, 0x111622)
      .setAlpha(0.45);

    // Body text
    this.add
      .text(width / 2, height * 0.5 + 10, panel.body, {
        fontFamily: FONT_MONO,
        fontSize: '22px',
        color: '#d0d4de',
        align: 'center',
        lineSpacing: 10,
        wordWrap: { width: width - 140 },
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5);

    // Panel counter (bottom-left)
    this.add
      .text(28, height - 26, `${index + 1} / ${this.panels.length}`, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: '#3a4050',
      })
      .setOrigin(0, 1);

    // Advance prompt (bottom-right)
    const isLast = index >= this.panels.length - 1;
    const promptText = this.add
      .text(width - 28, height - 26, isLast ? 'ANY KEY  ▶  CONTINUE' : 'ANY KEY  ▶', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: isLast ? accentHex : '#5a6070',
      })
      .setOrigin(1, 1);

    this.tweens.add({
      targets: promptText,
      alpha: { from: 1, to: 0.2 },
      duration: 680,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private fadeIn(): void {
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 280,
      ease: 'Quad.easeOut',
    });
  }

  private advance(): void {
    if (this.advancing) return;
    this.advancing = true;
    this.tweens.killAll();
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: 180,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.panelIndex += 1;
        this.advancing = false;
        if (this.panelIndex >= this.panels.length) {
          this.endCutscene();
        } else {
          this.renderPanel(this.panelIndex);
        }
      },
    });
  }

  private endCutscene(): void {
    this.scene.start(this.nextSceneKey, this.nextData);
  }
}
